import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Input, Row } from "reactstrap";
import { setBreadcrumbItems } from "../../store/Breadcrumb/actions";
import { changePreloader } from "../../store/actions";
import { formatDate } from '../../utility/formatDate';
import DropdownMenuBtn from '../Orders/DropdownMenuBtn';
import OrderStatusRenderer from '../Orders/OrderStatusRenderer';
import { getPurchaseRequisitionListReq } from '../../service/purchaseRequisitionService';

const AllRequisition = (props) => {
  document.title = "All Purchase Requisition";
  let dispatch = useDispatch();
  let navigate = useNavigate();

  const autoSizeStrategy = {
    type: 'fitGridWidth'
  };
  const paginationPageSizeSelector = [25, 50, 100];

  const [rowData, setRowData] = useState([]);
  const [paginationPageSize, setPaginationPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState();
  const [inputValue, setInputValue] = useState('');
  const [sortData, setSortData] = useState({
    key: 'createdAt',
    order: "desc"
  });

  const timerRef = useRef(null);

  const redirectToViewPage = (id) => {
    let path = `/purchase-requisitions/${id}`;
    setTimeout(() => {
      navigate(path, id);
    }, 300);
  };

  const handleViewClick = (id) => {
    redirectToViewPage(id.purchaseRequisitionId);
  }

  useEffect(() => {
    props.setBreadcrumbItems('Purchase Requisitions', breadcrumbItems);
  }, []);

  useEffect(() => {
    let body = {
      page: page,
      limit: paginationPageSize,
    }
    if (searchValue) {
      body.search = searchValue
    }
    if (sortData) {
      body.sort = sortData
    }
    getListOfRowData(body);
  }, [page, paginationPageSize, searchValue, sortData]);
  
  const getListOfRowData = useCallback(async (body) => {
    if (rowData[(page - 1) * paginationPageSize]) {
      return;
    }

    dispatch(changePreloader(true));

    try {
      const response = await getPurchaseRequisitionListReq(body);

      if (!response || !response.purchaseRequisitions || !Array.isArray(response.purchaseRequisitions)) {
        console.error("Unexpected response format:", response);
        return;
      }

      const res = response.purchaseRequisitions.map(requisition => {
        return {
          purchaseRequisitionId: requisition._id,
          purchaseRequisitionNumber: requisition.purchaseRequisitionNumber ? requisition.purchaseRequisitionNumber: "-",
          purchaseOrderNumber: requisition.purchaseOrderNumber ? requisition.purchaseOrderNumber : "-",
          client_name: requisition.clientId.name,
          createdAt: formatDate(requisition.createdAt),
          status: requisition.status,
        };
      });

      const emptyObjects = Array.from({ length: res.length }, () => null);
      let filledRows;
      if(res.length < paginationPageSize) {
        filledRows = [...res];
      } else {
        filledRows = [...res, ...emptyObjects]
      }

      // Replace the section of data for the current page
      const newData = [...rowData];

      newData.splice((page - 1) * paginationPageSize, paginationPageSize, ...filledRows);

      setRowData(newData);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
    } finally {
      dispatch(changePreloader(false));
    }
  }, [page, paginationPageSize, searchValue, sortData]);

  const redirectToEditPage = (id) => {
    let path = `/purchase-requisitions/${id.purchaseRequisitionId}/edit`;
    setTimeout(() => {
      navigate(path);
    }, 300);
  }

  const handleDeleteResponse = (response) => {
    if (response.success === true) {
      notify("Success", response.message);
    } else {
      notify("Error", response.message);
    }
    getListOfRowData();
  }

  const handleEditClick = (id) => {
    redirectToEditPage(id);
  }

  const onPaginationChanged = useCallback((event) => {
    // Workaround for bug in events order
    const pageSize = gridRef.current.api.paginationGetPageSize();

    if (pageSize !== paginationPageSize) {
      setPaginationPageSize(pageSize);
    }
    const newPage = gridRef.current.api.paginationGetCurrentPage() + 1;

    if (page !== newPage) {
      setPage(newPage);
    }
  }, [page, paginationPageSize]);


  const handleInputChange = (e) => {
    setInputValue(e.target.value?.trim());
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setRowData([]);
      setPage(1);
      setSearchValue(e.target.value?.trim());
    }, 2000);
  };

  const gridRef = useRef();
  
  const breadcrumbItems = [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Purchase Requisitions", link: "#" },
  ];

  const columnDefs = [
    {
      headerName: "PR No.", field: "purchaseRequisitionNumber", suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Purchase Requisition No.",
      sortable: true, minWidth: 150,
      comparator: () => false,
    },
    {
      headerName: "PO No.", field: "purchaseOrderNumber", suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Purchase Order No.",
      sortable: false, minWidth: 150,
      cellRenderer: (props) => {
        return <>{props.value ? props.value : "-" }</>
      },
      comparator: () => false,
    },
    {
      headerName: "Created Date", field: "createdAt", suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Created Date",
      sortable: true, minWidth: 150,
      comparator: () => false,
      sort: sortData.key === "createdAt" && sortData.order
    },
    {
      headerName: "Status", field: "status", suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Requisition Status", cellRenderer: OrderStatusRenderer,
      sortable: true, minWidth: 150,
      comparator: () => false,
    },
    {
      headerName: "Action", field: "action", sortable: false, width: 100,
      cellClass: "actions-button-cell",
      cellRenderer: DropdownMenuBtn,
      cellRendererParams: {
        handleResponse: handleDeleteResponse,
        handleEditClick: handleEditClick,
        handleViewClick: handleViewClick,
      }, suppressMenu: true, floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Actions",
    }
  ];

  const notify = (type, message) => {
    if (type === "Error") {
      toast.error(message, {
        position: "top-center",
        theme: "colored",
      })
    } else {
      toast.success(message, {
        position: "top-center",
        theme: "colored",
      })
    }
  }

  const handleSortChange = (e) => {
    const columns = gridRef.current.api.getColumnState();
    const ele = columns.find(
      (ele) => ele.sort === "asc" || ele.sort === "desc"
    );

    if (ele) {
      setRowData([]);
      setPage(1);
      setSortData({
        key: ele.colId,
        order: ele.sort,
      });
    }
  };

  return (
    <>
      <div className="all-items">
        <Row>
          <Col className="col-12">
            <Card>
              <CardBody>
                <div className="button-section">
                  <div className="button-right-section">
                    <div className="invoice-search-box">
                        <div className="search-box position-relative" style={{ width: '16rem' }}>
                          <Input
                            type="text"
                            value={inputValue}
                            onChange={handleInputChange}
                            className="form-control rounded border"
                            placeholder={"Search by Requisition Number"}

                          />
                          <i className="mdi mdi-magnify search-icon"></i>
                        </div>
                    </div>
                  </div>
                </div>
                <div
                  className="ag-theme-quartz"
                  style={{
                    height: '550px',
                    width: '100%'
                  }}
                >
                    <AgGridReact
                      ref={gridRef}
                      suppressRowClickSelection={true}
                      columnDefs={columnDefs}
                      pagination={true}
                      paginationPageSize={paginationPageSize}
                      paginationPageSizeSelector={paginationPageSizeSelector}
                      autoSizeStrategy={autoSizeStrategy}
                      rowData={rowData}
                      reactiveCustomComponents
                      onPaginationChanged={onPaginationChanged}
                      onSortChanged={handleSortChange}
                      sortingOrder={["desc", "asc"]}
                    >
                    </AgGridReact>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default connect(null, { setBreadcrumbItems })(AllRequisition);
